import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hms_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to admin login if we're on an admin route
      // Guest portal handles its own auth redirects
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/portal')) {
        localStorage.removeItem('hms_token');
        localStorage.removeItem('hms_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
};

// Tenants API
export const tenantsApi = {
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.patch(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
};

// Properties API
export const propertiesApi = {
  list: () => api.get('/properties'),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.patch(`/properties/${id}`, data),
  activate: (id) => api.post(`/properties/${id}/activate`),
};

// Users API
export const usersApi = {
  list: () => api.get('/users'),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  assignRole: (userId, roleId) => api.post(`/users/${userId}/roles/${roleId}`),
  removeRole: (userId, roleId) => api.delete(`/users/${userId}/roles/${roleId}`),
};

// Roles API
export const rolesApi = {
  list: () => api.get('/roles'),
  get: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.patch(`/roles/${id}`, data),
};

// Permissions API
export const permissionsApi = {
  list: (category) => api.get('/permissions', { params: { category } }),
};

// Audit API
export const auditApi = {
  list: (params) => api.get('/audit', { params }),
  get: (id) => api.get(`/audit/${id}`),
  stats: (days) => api.get('/audit/stats', { params: { days } }),
};

// Rooms API
export const roomsApi = {
  listTypes: (propertyId) => api.get('/rooms/types', { params: { property_id: propertyId } }),
  createType: (propertyId, data) => api.post('/rooms/types', data, { params: { property_id: propertyId } }),
  list: (propertyId, params) => api.get('/rooms', { params: { property_id: propertyId, ...params } }),
  create: (propertyId, data) => api.post('/rooms', data, { params: { property_id: propertyId } }),
  updateStatus: (roomId, status, reason) => api.patch(`/rooms/${roomId}/status`, null, { params: { maintenance_status: status, reason } }),
};

// Dashboard API
export const dashboardApi = {
  getStats: (propertyId) => api.get('/dashboard/stats', { params: { property_id: propertyId } }),
  getRecentActivity: (limit) => api.get('/dashboard/recent-activity', { params: { limit } }),
};

// Guests API
export const guestsApi = {
  list: (params) => api.get('/guests', { params }),
  get: (id) => api.get(`/guests/${id}`),
  create: (data) => api.post('/guests', data),
  update: (id, data) => api.patch(`/guests/${id}`, data),
  getHistory: (id) => api.get(`/guests/${id}/history`),
};

// Rates API
export const ratesApi = {
  listPlans: (propertyId, params) => api.get('/rates/plans', { params: { property_id: propertyId, ...params } }),
  getPlan: (id) => api.get(`/rates/plans/${id}`),
  createPlan: (propertyId, data) => api.post('/rates/plans', data, { params: { property_id: propertyId } }),
  updatePlan: (id, data) => api.patch(`/rates/plans/${id}`, data),
  setRoomRate: (planId, data) => api.post(`/rates/plans/${planId}/room-rates`, data),
  listRestrictions: (propertyId, params) => api.get('/rates/restrictions', { params: { property_id: propertyId, ...params } }),
  createRestriction: (propertyId, data) => api.post('/rates/restrictions', data, { params: { property_id: propertyId } }),
  deleteRestriction: (id) => api.delete(`/rates/restrictions/${id}`),
};

// Availability API
export const availabilityApi = {
  search: (propertyId, data) => api.post('/availability/search', data, { params: { property_id: propertyId } }),
  getCalendar: (propertyId, startDate, endDate, roomTypeId) => 
    api.get('/availability/calendar', { params: { property_id: propertyId, start_date: startDate, end_date: endDate, room_type_id: roomTypeId } }),
};

// Reservations API
export const reservationsApi = {
  list: (propertyId, params) => api.get('/reservations', { params: { property_id: propertyId, ...params } }),
  get: (id) => api.get(`/reservations/${id}`),
  create: (propertyId, data) => api.post('/reservations', data, { params: { property_id: propertyId } }),
  update: (id, data) => api.patch(`/reservations/${id}`, data),
  cancel: (id, data) => api.post(`/reservations/${id}/cancel`, data),
  checkIn: (id, roomId) => api.post(`/reservations/${id}/checkin`, null, { params: { room_id: roomId } }),
  checkOut: (id) => api.post(`/reservations/${id}/checkout`),
  getArrivals: (propertyId, date) => api.get('/reservations/arrivals', { params: { property_id: propertyId, date } }),
  getDepartures: (propertyId, date) => api.get('/reservations/departures', { params: { property_id: propertyId, date } }),
  getInhouse: (propertyId) => api.get('/reservations/inhouse', { params: { property_id: propertyId } }),
  getStats: (propertyId, date) => api.get('/reservations/stats/summary', { params: { property_id: propertyId, date } }),
  availableRooms: (propertyId, params) => api.get('/reservations/available-rooms', { params: { property_id: propertyId, ...params } }),
};

// Front Office API
export const frontOfficeApi = {
  getDashboard: (propertyId, businessDate) => 
    api.get('/front-office/dashboard', { params: { property_id: propertyId, business_date: businessDate } }),
  checkIn: (reservationId, propertyId, data) => 
    api.post(`/front-office/check-in/${reservationId}`, data, { params: { property_id: propertyId } }),
  checkOut: (reservationId, propertyId, data) => 
    api.post(`/front-office/check-out/${reservationId}`, data, { params: { property_id: propertyId } }),
  getRoomStatus: (propertyId, floor) => 
    api.get('/front-office/room-status', { params: { property_id: propertyId, floor } }),
  updateRoomStatus: (roomId, data) => 
    api.patch(`/front-office/room-status/${roomId}`, data),
  assignRoom: (data) => 
    api.post('/front-office/assign-room', data),
};

// Folios API
export const foliosApi = {
  list: (propertyId, params) => api.get('/folios', { params: { property_id: propertyId, ...params } }),
  get: (id) => api.get(`/folios/${id}`),
  create: (propertyId, data) => api.post('/folios', data, { params: { property_id: propertyId } }),
  postCharge: (folioId, data) => api.post(`/folios/${folioId}/charges`, data),
  postPayment: (folioId, data) => api.post(`/folios/${folioId}/payments`, data),
  postAdjustment: (folioId, data) => api.post(`/folios/${folioId}/adjustments`, data),
  voidTransaction: (transactionId, data) => api.post(`/folios/transactions/${transactionId}/void`, data),
  settle: (folioId) => api.post(`/folios/${folioId}/settle`),
  getChargeCodes: (propertyId, category) => api.get('/folios/charge-codes', { params: { property_id: propertyId, category } }),
  getPrintData: (folioId) => api.get(`/folios/${folioId}/print`),
};

// Housekeeping API
export const housekeepingApi = {
  getDashboard: (propertyId, businessDate) => 
    api.get('/housekeeping/dashboard', { params: { property_id: propertyId, business_date: businessDate } }),
  listTasks: (propertyId, params) => 
    api.get('/housekeeping/tasks', { params: { property_id: propertyId, ...params } }),
  createTask: (propertyId, data) => 
    api.post('/housekeeping/tasks', data, { params: { property_id: propertyId } }),
  updateTask: (taskId, data) => 
    api.patch(`/housekeeping/tasks/${taskId}`, data),
  getRooms: (propertyId, params) => 
    api.get('/housekeeping/rooms', { params: { property_id: propertyId, ...params } }),
  updateRoomStatus: (roomId, data) => 
    api.patch(`/housekeeping/rooms/${roomId}/status`, data),
  generateTasks: (propertyId, businessDate) => 
    api.post('/housekeeping/generate-tasks', null, { params: { property_id: propertyId, business_date: businessDate } }),
  getAttendants: (propertyId, businessDate) => 
    api.get('/housekeeping/attendants', { params: { property_id: propertyId, business_date: businessDate } }),
};

// Finance API
export const financeApi = {
  getDashboard: (propertyId, businessDate) => 
    api.get('/finance/dashboard', { params: { property_id: propertyId, business_date: businessDate } }),
  listInvoices: (propertyId, params) => 
    api.get('/finance/invoices', { params: { property_id: propertyId, ...params } }),
  getInvoice: (id) => api.get(`/finance/invoices/${id}`),
  createInvoice: (propertyId, data) => 
    api.post('/finance/invoices', data, { params: { property_id: propertyId } }),
  issueInvoice: (id) => api.post(`/finance/invoices/${id}/issue`),
  recordPayment: (invoiceId, propertyId, data) => 
    api.post(`/finance/invoices/${invoiceId}/payment`, data, { params: { property_id: propertyId } }),
  getARSummary: (propertyId, companyId) => 
    api.get('/finance/ar', { params: { property_id: propertyId, company_id: companyId } }),
  getARLedger: (companyId, propertyId, params) => 
    api.get(`/finance/ar/${companyId}/ledger`, { params: { property_id: propertyId, ...params } }),
  getDailyRevenue: (propertyId, fromDate, toDate) => 
    api.get('/finance/revenue/daily', { params: { property_id: propertyId, from_date: fromDate, to_date: toDate } }),
  getRevenueSummary: (propertyId, fromDate, toDate) => 
    api.get('/finance/revenue/summary', { params: { property_id: propertyId, from_date: fromDate, to_date: toDate } }),
  listReceipts: (propertyId, params) => 
    api.get('/finance/receipts', { params: { property_id: propertyId, ...params } }),
};

// POS API
export const posApi = {
  getDashboard: (propertyId, outletId, businessDate) => 
    api.get('/pos/dashboard', { params: { property_id: propertyId, outlet_id: outletId, business_date: businessDate } }),
  listOutlets: (propertyId) => 
    api.get('/pos/outlets', { params: { property_id: propertyId } }),
  createOutlet: (propertyId, data) => 
    api.post('/pos/outlets', null, { params: { property_id: propertyId, ...data } }),
  getMenu: (outletId, categoryId) => 
    api.get(`/pos/menu/${outletId}`, { params: { category_id: categoryId } }),
  getMenuCategories: (outletId) => 
    api.get(`/pos/menu/${outletId}/categories`),
  createMenuItem: (outletId, data) => 
    api.post(`/pos/menu/${outletId}/items`, data),
  createMenuCategory: (outletId, data) => 
    api.post(`/pos/menu/${outletId}/categories`, null, { params: data }),
  listChecks: (propertyId, params) => 
    api.get('/pos/checks', { params: { property_id: propertyId, ...params } }),
  getCheck: (checkId) => 
    api.get(`/pos/checks/${checkId}`),
  createCheck: (propertyId, data) => 
    api.post('/pos/checks', data, { params: { property_id: propertyId } }),
  addCheckItem: (checkId, data) => 
    api.post(`/pos/checks/${checkId}/items`, data),
  payCheck: (checkId, propertyId, data) => 
    api.post(`/pos/checks/${checkId}/pay`, data, { params: { property_id: propertyId } }),
  voidCheck: (checkId, reason) => 
    api.post(`/pos/checks/${checkId}/void`, null, { params: { reason } }),
};

// Maintenance API
export const maintenanceApi = {
  getDashboard: (propertyId) => 
    api.get('/maintenance/dashboard', { params: { property_id: propertyId } }),
  listOrders: (propertyId, params) => 
    api.get('/maintenance/orders', { params: { property_id: propertyId, ...params } }),
  getOrder: (orderId) => 
    api.get(`/maintenance/orders/${orderId}`),
  createOrder: (propertyId, data) => 
    api.post('/maintenance/orders', data, { params: { property_id: propertyId } }),
  updateOrder: (orderId, data) => 
    api.patch(`/maintenance/orders/${orderId}`, data),
  addComment: (orderId, comment) => 
    api.post(`/maintenance/orders/${orderId}/comment`, null, { params: { comment } }),
  listCategories: (propertyId) => 
    api.get('/maintenance/categories', { params: { property_id: propertyId } }),
  createCategory: (propertyId, data) => 
    api.post('/maintenance/categories', data, { params: { property_id: propertyId } }),
  getTechnicians: (propertyId) => 
    api.get('/maintenance/technicians', { params: { property_id: propertyId } }),
};

// Phase E (No-AI) — Global Search + Insights
export const searchApi = {
  query: (propertyId, q) => api.get('/search', { params: { property_id: propertyId, q } }),
};
export const insightsApi = {
  forecast: (propertyId, metric, days) => api.get('/insights/forecast', { params: { property_id: propertyId, metric, days } }),
  recommendations: (propertyId) => api.get('/insights/recommendations', { params: { property_id: propertyId } }),
};

// Phase D — Stores & Expiry API (#10)
export const storesApi = {
  list: (propertyId) => api.get('/stores', { params: { property_id: propertyId } }),
  create: (propertyId, data) => api.post('/stores', data, { params: { property_id: propertyId } }),
  update: (id, data) => api.patch(`/stores/${id}`, data),
  openOn: (propertyId, onDate) => api.get('/stores/open', { params: { property_id: propertyId, on_date: onDate } }),
  listHolidays: (propertyId) => api.get('/stores/holidays', { params: { property_id: propertyId } }),
  addHoliday: (propertyId, data) => api.post('/stores/holidays', data, { params: { property_id: propertyId } }),
  deleteHoliday: (id) => api.delete(`/stores/holidays/${id}`),
  listBatches: (propertyId, params) => api.get('/stores/batches', { params: { property_id: propertyId, ...params } }),
  createBatch: (propertyId, data) => api.post('/stores/batches', data, { params: { property_id: propertyId } }),
  expiryAlerts: (propertyId, days) => api.get('/stores/expiry-alerts', { params: { property_id: propertyId, days } }),
};

// Phase D — Outsourcing API (#4)
export const outsourcingApi = {
  listPartners: (propertyId) => api.get('/outsourcing/partners', { params: { property_id: propertyId } }),
  createPartner: (propertyId, data) => api.post('/outsourcing/partners', data, { params: { property_id: propertyId } }),
  listRecords: (propertyId, status) => api.get('/outsourcing/records', { params: { property_id: propertyId, status } }),
  createRecord: (propertyId, data) => api.post('/outsourcing/records', data, { params: { property_id: propertyId } }),
  updateRecord: (id, data) => api.patch(`/outsourcing/records/${id}`, data),
  dashboard: (propertyId) => api.get('/outsourcing/dashboard', { params: { property_id: propertyId } }),
};

// Phase D — Surveys API (#5)
export const surveysApi = {
  list: (propertyId) => api.get('/surveys', { params: { property_id: propertyId } }),
  get: (id) => api.get(`/surveys/${id}`),
  create: (propertyId, data) => api.post('/surveys', data, { params: { property_id: propertyId } }),
  update: (id, data) => api.patch(`/surveys/${id}`, data),
  submitResponse: (id, data) => api.post(`/surveys/${id}/responses`, data),
  listResponses: (id) => api.get(`/surveys/${id}/responses`),
  results: (id) => api.get(`/surveys/${id}/results`),
};

// Accounting (GL) API (Phase C, #6)
export const accountingApi = {
  listAccounts: (propertyId, accountType) => api.get('/accounting/accounts', { params: { property_id: propertyId, account_type: accountType } }),
  createAccount: (propertyId, data) => api.post('/accounting/accounts', data, { params: { property_id: propertyId } }),
  seedCoa: (propertyId) => api.post('/accounting/seed-coa', null, { params: { property_id: propertyId } }),
  listPeriods: (propertyId) => api.get('/accounting/periods', { params: { property_id: propertyId } }),
  createPeriod: (propertyId, data) => api.post('/accounting/periods', data, { params: { property_id: propertyId } }),
  closePeriod: (id) => api.post(`/accounting/periods/${id}/close`),
  listVouchers: (propertyId, params) => api.get('/accounting/vouchers', { params: { property_id: propertyId, ...params } }),
  getVoucher: (id) => api.get(`/accounting/vouchers/${id}`),
  createVoucher: (propertyId, data) => api.post('/accounting/vouchers', data, { params: { property_id: propertyId } }),
  postVoucher: (id) => api.post(`/accounting/vouchers/${id}/post`),
  voidVoucher: (id) => api.post(`/accounting/vouchers/${id}/void`),
  trialBalance: (propertyId, dateTo) => api.get('/accounting/trial-balance', { params: { property_id: propertyId, date_to: dateTo } }),
  incomeStatement: (propertyId, dateFrom, dateTo) => api.get('/accounting/income-statement', { params: { property_id: propertyId, date_from: dateFrom, date_to: dateTo } }),
  balanceSheet: (propertyId, dateTo) => api.get('/accounting/balance-sheet', { params: { property_id: propertyId, date_to: dateTo } }),
  yearEndClose: (propertyId, fiscalYear) => api.post('/accounting/year-end-close', null, { params: { property_id: propertyId, fiscal_year: fiscalYear } }),
};

// Credit & Deposits API (Phase C, #3)
export const creditApi = {
  listAccounts: (propertyId) => api.get('/credit/accounts', { params: { property_id: propertyId } }),
  createAccount: (propertyId, data) => api.post('/credit/accounts', data, { params: { property_id: propertyId } }),
  statement: (id) => api.get(`/credit/accounts/${id}/statement`),
  addEntry: (id, data) => api.post(`/credit/accounts/${id}/entries`, data),
  aging: (propertyId) => api.get('/credit/aging', { params: { property_id: propertyId } }),
};

// Training Center API (Phase B)
export const tcApi = {
  listVenues: (propertyId) => api.get('/tc/venues', { params: { property_id: propertyId } }),
  createVenue: (propertyId, data) => api.post('/tc/venues', data, { params: { property_id: propertyId } }),
  updateVenue: (id, data) => api.patch(`/tc/venues/${id}`, data),
  venueAvailability: (propertyId, startDate, endDate, pax) =>
    api.get('/tc/venues/availability', { params: { property_id: propertyId, start_date: startDate, end_date: endDate, pax } }),
  listBookings: (propertyId, status) => api.get('/tc/bookings', { params: { property_id: propertyId, status } }),
  getBooking: (id) => api.get(`/tc/bookings/${id}`),
  createBooking: (propertyId, data) => api.post('/tc/bookings', data, { params: { property_id: propertyId } }),
  updateBooking: (id, data) => api.patch(`/tc/bookings/${id}`, data),
  cancelBooking: (id) => api.post(`/tc/bookings/${id}/cancel`),
  dashboard: (propertyId) => api.get('/tc/dashboard', { params: { property_id: propertyId } }),
  // Lodging linkage (Phase B integration)
  bookingLodging: (id) => api.get(`/tc/bookings/${id}/lodging`),
  allocateLodging: (id, roomIds) => api.post(`/tc/bookings/${id}/allocate-lodging`, { room_ids: roomIds || null }),
  releaseLodging: (id) => api.post(`/tc/bookings/${id}/release-lodging`),
  outsourceOverflow: (id, data) => api.post(`/tc/bookings/${id}/outsource`, data),
};

// F&B Cost Control API (WP4 - TOR §C.6)
export const fnbApi = {
  listIngredients: (propertyId) => api.get('/fnb/ingredients', { params: { property_id: propertyId } }),
  listRecipes: (propertyId, category) => api.get('/fnb/recipes', { params: { property_id: propertyId, category } }),
  getRecipe: (id) => api.get(`/fnb/recipes/${id}`),
  createRecipe: (propertyId, data) => api.post('/fnb/recipes', data, { params: { property_id: propertyId } }),
  updateRecipe: (id, data) => api.put(`/fnb/recipes/${id}`, data),
  deleteRecipe: (id) => api.delete(`/fnb/recipes/${id}`),
  listWaste: (propertyId, params) => api.get('/fnb/waste', { params: { property_id: propertyId, ...params } }),
  recordWaste: (propertyId, data) => api.post('/fnb/waste', data, { params: { property_id: propertyId } }),
  dashboard: (propertyId, days) => api.get('/fnb/dashboard', { params: { property_id: propertyId, days } }),
};

// Payments API (WP2 - TOR §A.8: Telebirr/CBE/Chapa)
export const paymentsApi = {
  listProviders: () => api.get('/payments/providers'),
  initiate: (propertyId, data) => api.post('/payments/initiate', data, { params: { property_id: propertyId } }),
  get: (paymentId) => api.get(`/payments/${paymentId}`),
  list: (propertyId, folioId) => api.get('/payments', { params: { property_id: propertyId, folio_id: folioId } }),
  simulateConfirm: (paymentId) => api.post(`/payments/${paymentId}/simulate-confirm`),
};

// Night Audit API (WP1 - TOR §E.1)
export const nightAuditApi = {
  getBusinessDate: (propertyId) =>
    api.get('/night-audit/business-date', { params: { property_id: propertyId } }),
  preview: (propertyId) =>
    api.post('/night-audit/preview', null, { params: { property_id: propertyId } }),
  run: (propertyId, autoNoShow = true) =>
    api.post('/night-audit/run', null, { params: { property_id: propertyId, auto_no_show: autoNoShow } }),
  listRuns: (propertyId, limit = 30) =>
    api.get('/night-audit/runs', { params: { property_id: propertyId, limit } }),
  getRun: (runId) => api.get(`/night-audit/runs/${runId}`),
};

// Guest Portal API (public endpoints)
export const guestPortalApi = {
  // Auth
  register: (data) => api.post('/guest-portal/auth/register', data),
  login: (data) => api.post('/guest-portal/auth/login', data),
  getProfile: (token) => api.get('/guest-portal/auth/me', { headers: { Authorization: `Bearer ${token}` } }),
  updateProfile: (token, data) => api.patch('/guest-portal/auth/me', data, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Properties
  listProperties: () => api.get('/guest-portal/properties'),
  getProperty: (propertyId) => api.get(`/guest-portal/properties/${propertyId}`),
  
  // Availability & Booking
  searchAvailability: (data) => api.post('/guest-portal/availability/search', data),
  createReservation: (token, data) => api.post('/guest-portal/reservations', data, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Reservations
  listReservations: (token) => api.get('/guest-portal/reservations', { headers: { Authorization: `Bearer ${token}` } }),
  getReservation: (token, id) => api.get(`/guest-portal/reservations/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  cancelReservation: (token, id, reason) => api.post(`/guest-portal/reservations/${id}/cancel`, null, { headers: { Authorization: `Bearer ${token}` }, params: { reason } }),
  
  // Pre-Check-In
  getPreCheckin: (token, reservationId) => api.get(`/guest-portal/reservations/${reservationId}/pre-checkin`, { headers: { Authorization: `Bearer ${token}` } }),
  submitPreCheckin: (token, reservationId, data) => api.post(`/guest-portal/reservations/${reservationId}/pre-checkin`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Service Requests
  listServiceRequests: (token) => api.get('/guest-portal/service-requests', { headers: { Authorization: `Bearer ${token}` } }),
  createServiceRequest: (token, data) => api.post('/guest-portal/service-requests', data, { headers: { Authorization: `Bearer ${token}` } }),
  getServiceRequest: (token, id) => api.get(`/guest-portal/service-requests/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  submitFeedback: (token, id, rating, feedback) => api.post(`/guest-portal/service-requests/${id}/feedback`, null, { headers: { Authorization: `Bearer ${token}` }, params: { rating, feedback } }),
  
  // Folio
  getFolio: (token, reservationId) => api.get(`/guest-portal/reservations/${reservationId}/folio`, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Favorites
  listFavorites: (token) => api.get('/guest-portal/favorites', { headers: { Authorization: `Bearer ${token}` } }),
  addFavorite: (token, propertyId) => api.post(`/guest-portal/favorites/${propertyId}`, null, { headers: { Authorization: `Bearer ${token}` } }),
  removeFavorite: (token, propertyId) => api.delete(`/guest-portal/favorites/${propertyId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Spa Services (new)
  listSpaServices: (propertyId) => api.get('/guest-portal/spa/services', { params: { property_id: propertyId } }),
  checkSpaAvailability: (propertyId, serviceId, date) => api.get('/guest-portal/spa/availability', { params: { property_id: propertyId, service_id: serviceId, date } }),
  bookSpa: (token, data) => api.post('/guest-portal/spa/book', data, { headers: { Authorization: `Bearer ${token}` } }),
  listMySpaAppointments: (token) => api.get('/guest-portal/spa/my-appointments', { headers: { Authorization: `Bearer ${token}` } }),
  cancelSpaAppointment: (token, appointmentId, reason) => api.post(`/guest-portal/spa/appointments/${appointmentId}/cancel`, null, { headers: { Authorization: `Bearer ${token}` }, params: { reason } }),
  
  // Events (new)
  listUpcomingEvents: (propertyId) => api.get('/guest-portal/events/upcoming', { params: { property_id: propertyId } }),
  listFunctionSpaces: (propertyId) => api.get('/guest-portal/events/function-spaces', { params: { property_id: propertyId } }),
  submitEventInquiry: (data) => api.post('/guest-portal/events/inquire', data),
  
  // Loyalty & Rewards
  getLoyaltyStatus: (token) => api.get('/guest-portal/loyalty/status', { headers: { Authorization: `Bearer ${token}` } }),
  getLoyaltyTransactions: (token) => api.get('/guest-portal/loyalty/transactions', { headers: { Authorization: `Bearer ${token}` } }),
  getAvailableRewards: (token) => api.get('/guest-portal/loyalty/rewards', { headers: { Authorization: `Bearer ${token}` } }),
  redeemReward: (token, rewardId) => api.post(`/guest-portal/loyalty/redeem?reward_id=${rewardId}`, null, { headers: { Authorization: `Bearer ${token}` } }),
};

export default api;
